import { Box, Typography } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import { cardPumpStatus, cardPumpImage, cardPumpMode } from '../style';
import { pump, StyledCardPumpStatus, modeValueStyle } from './style';
import { AlarmKind } from '../../types';
import { CRANE_PUMP, PUMP_1, PUMP_2, PUMP_3 } from 'src/features/sites';
import { PumpAsset } from 'src/types/assets';
import { PropelerPumpIcon } from '../PropelerPumpIcon';
import { RegularPumpWithPropeler } from '../RegularPumpWithPropeler';
import { useGetMultipleLatestReadings } from 'src/hooks/apiHooks/useGetMultipleLatestReadings';
import { useMemo } from 'react';
import { CranePumpWidget } from 'src/assets/widgets/CranePumpWidget';
import { DosingPumpIcon } from 'src/assets/icons/DosingPumpIcon';
import { PumpIcon } from 'src/assets/icons/PumpIcon';
import { usePumpStateAndMode } from 'src/hooks/featureHooks/usePumpStateAndMode';
import { useAssetStateData } from 'src/features/sites/hooks';
import { useAssetModeData } from 'src/features/sites/hooks/useAssetModeData';
import { MODE } from 'src/constants';

type Props = {
  title: string;
  alarm?: AlarmKind;
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  widgetSubType?: string;
  asset: PumpAsset;
  isLoading?: boolean;
};

export const Pump = ({
  title,
  isEditMode,
  widgetId,
  toggleAddOrRemoveHandler,
  testId,
  widgetSubType,
  asset,
  isLoading,
}: Props) => {
  const {
    assetState: liveActiveState,
    assetActiveAlarm,
    isRunningLive,
  } = useAssetStateData({ asset: asset! });

  const renderWidgetIcon = (isRunning: boolean, isAlarmActive: boolean) => {
    const iconWidth = 70;
    const iconHeight = 70;
    if (widgetSubType === PUMP_1)
      return (
        <RegularPumpWithPropeler
          width={90}
          height={90}
          isRunMode={isRunning}
          isAlarmActive={isAlarmActive}
          propelerWidth={25}
        />
      );
    if (widgetSubType === PUMP_2) return <DosingPumpIcon width={iconWidth} height={iconHeight} />;
    if (widgetSubType === PUMP_3)
      return (
        <PropelerPumpIcon isRunMode={isRunning} isAlarmActive={isAlarmActive} showRotator={true} />
      );
    if (widgetSubType === CRANE_PUMP) return <CranePumpWidget width={50} height={90} />;
    return <PumpIcon />;
  };

  const { activeState, isRunning, activeMode } = usePumpStateAndMode({ asset: asset });
  const { assetMode: liveAssetMode } = useAssetModeData({ asset: asset });

  const { readingsMap: multipleReadings, isDataReady: isMultipleDataReady } =
    useGetMultipleLatestReadings({
      items: asset?.assignedAlarmTags,
    });

  const values = Object.values(multipleReadings)?.map((tagData) => {
    const tagId = Object.keys(tagData)[0];
    const values = tagData[tagId]?.values ?? {};
    return Object.values(values)[0];
  });

  const isAlarmActive: boolean =
    assetActiveAlarm !== null ? assetActiveAlarm === 1 : values.some((item) => Number(item) > 0);

  const isAlarmReady = useMemo(
    () =>
      isMultipleDataReady &&
      (isAlarmActive !== undefined || isAlarmActive === false || isAlarmActive),
    [isMultipleDataReady, isAlarmActive]
  );

  const isReady = isMultipleDataReady && isAlarmReady;

  const isPumpMappedToAlarmTags = asset?.assignedAlarmTags?.some((tag) => tag.assignedAlarmTagId);
  const isPumpMappedToMode = asset?.assignedTags?.some(
    (tag) => tag?.tagName?.toLowerCase() === MODE
  );

  const displayedState = liveActiveState !== null ? liveActiveState : activeState;
  const displayMode = liveAssetMode !== null ? liveAssetMode : activeMode;

  const currentIsRunning = isRunningLive ?? isRunning;

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={!isLoading && isReady}
      isPumpWidget={true}
      isAlarmForPumpActive={isAlarmActive}
      isPumpMappedToAlarmTags={isPumpMappedToAlarmTags}
    >
      <Box sx={pump}>
        <Box sx={cardPumpImage}>{renderWidgetIcon(Boolean(currentIsRunning), isAlarmActive)}</Box>

        <Box sx={cardPumpStatus}>
          <StyledCardPumpStatus isRunning={Boolean(currentIsRunning)} variant="contained">
            {displayedState ?? 'Undefined'}
          </StyledCardPumpStatus>
        </Box>

        <Box sx={cardPumpMode}>
          {isPumpMappedToMode ? (
            <Box>
              <Typography>Mode: </Typography>
              <Typography sx={modeValueStyle} data-sm={displayMode ? displayMode : '-'}>
                {displayMode ? displayMode : '-'}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </WidgetWrapper>
  );
};
